import React, { useMemo } from 'react';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { enUS } from 'date-fns/locale';
import { format as formatTz } from 'date-fns-tz';

import usePersistentFilters, {
  dateParser,
  dateTimeParser,
  toCentralDateParam,
} from 'hooks/usePersistentFilters';
import { getGridStringOperators, GridFilterInputValue } from '@mui/x-data-grid-premium';
import { formatNumber } from 'utils/helpers/format';
import PersistentDataGrid from 'common/PersistentDataGrid';
import usePersistentState from 'hooks/usePersistentState';
import {
  useGetBillsOfLadingInventoryOwnerQuery,
  useGetBillsOfLadingQuery,
  useGetBookingHistoryQuery,
  useGetCurrentInventoryDetailsQuery,
  useGetCurrentInventorySummaryQuery,
  useGetHistoricalInventoryQuery,
  useGetInventoryProjectionsReportQuery,
} from '../dashboardEndpoints';

import FilterPanel from './FilterPanel';

import BillsOfLadingColumns from './BillsOfLadingColumns';
import InventoryProjectionsColumns from './InventoryProjectionsColumns';
import InventoryDetailsColumns from './InventoryDetailsColumns';
import InventorySummaryColumns from './InventorySummaryColumns';
import BookingHistoryColumns from './BookingHistoryColumns';
import HistoricalInventoryColumns from './HistoricalInventoryColumns';

const doesNotIncludeStringOperator = {
  InputComponent: props => React.createElement(GridFilterInputValue, props),
  getApplyFilterFn: filterItem => {
    if (!filterItem.value) return null;

    const filterItemValue = filterItem.value.trim();

    return ({ value }) => {
      return value != null
        ? !(R.toLower(value.toString()).search(R.toLower(filterItemValue)) > -1)
        : false;
    };
  },
  label: 'does not contain',
  value: 'doesNotContain',
};

const appendCustomOperators = columnData => {
  if (columnData.type === 'number') return columnData;

  const filterOperators = R.insert(0, doesNotIncludeStringOperator, getGridStringOperators());
  return {
    ...columnData,
    filterOperators,
  };
};

const allReports = [
  {
    type: 'Bill of Lading (by Inventory Owner)',
    value: 'BillOfLadingInvOwner',
  },
  {
    type: 'Bill of Lading (by Supplier)',
    value: 'BillOfLadingSupplier',
  },
  {
    type: 'Inventory Projections',
    value: 'InventoryProjections',
  },
  {
    type: 'Current Details',
    value: 'InventoryDetails',
  },
  {
    type: 'Booking History',
    value: 'BookingHistory',
  },
  {
    type: 'Historical Inventory',
    value: 'HistoricalInventory',
  },
  {
    type: 'Current Summary',
    value: 'InventorySummary',
  },
];

const Reports = () => {
  const [reportType, setReportType] = usePersistentState(
    'inventoryReportsReportType',
    allReports[0]
  );
  const [filters, setFilters] = usePersistentFilters(
    'inventoryReports',
    allReports.reduce((acc, { value }) => {
      acc[value] = {
        allocationGroup: null,
        endDate: null,
        productGroup: null,
        startDate: null,
      };
      return acc;
    }, {}),
    {
      BillOfLadingInvOwner: {
        endDate: dateTimeParser,
        startDate: dateTimeParser,
      },
      BillOfLadingSupplier: {
        endDate: dateTimeParser,
        startDate: dateTimeParser,
      },
      BookingHistory: {
        endDate: dateParser,
        startDate: dateParser,
      },
      HistoricalInventory: {
        endDate: dateParser,
        startDate: dateParser,
      },
    }
  );
  const currentFilters = R.prop(R.prop('value', reportType), filters);
  const { currentAffiliate } = useUserRolePermissions();
  const allocationGroup = R.prop('allocationGroup', currentFilters);
  const productGroup = R.prop('productGroup', currentFilters);
  const endDate = R.prop('endDate', currentFilters);
  const startDate = R.prop('startDate', currentFilters);
  const isHistorical = R.any(R.equals(true))([
    R.equals('BookingHistory', R.prop('value', reportType)),
    R.equals('HistoricalInventory', R.prop('value', reportType)),
    R.includes('BillOfLading', R.prop('value', reportType) || ''),
  ]);
  const datesSelected = R.and(R.not(R.isEmpty(startDate)), R.not(R.isEmpty(endDate)));

  const optionalParams = {
    endDate: R.and(isHistorical, R.not(R.isNil(endDate)))
      ? toCentralDateParam(endDate, 'end')
      : null,
    startDate: R.and(isHistorical, R.not(R.isNil(startDate)))
      ? toCentralDateParam(startDate, 'start')
      : null,
  };
  const filtered = R.reject(R.equals(null))(optionalParams);

  const params = {
    ...filtered,
    allocationGroupId: R.prop('groupId', allocationGroup),
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    productSummaryGroupIds: R.prop('groupId', productGroup),
  };

  const {
    data: billsOfLadingData = [],
    isError: isBillsOfLadingError,
    isFetching: isFetchingBillsOfLading = false,
    fulfilledTimeStamp: billsOfLadingTimestamp,
  } = useGetBillsOfLadingQuery(
    {
      params: {
        ...params,
        inventoryOwnerFlag: false,
      },
    },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.includes('BillOfLadingSupplier', R.prop('value', reportType) || '')),
      ]),
    }
  );

  const {
    data: billsOfLadingInventoryOwnerData = [],
    isError: isBillsOfLadingInventoryOwnerError,
    isFetching: isFetchingBillsOfLadingInventoryOwner = false,
    fulfilledTimeStamp: billsOfLadingInventoryOwnerTimestamp,
  } = useGetBillsOfLadingInventoryOwnerQuery(
    {
      params: {
        ...params,
        inventoryOwnerFlag: true,
      },
    },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.includes('BillOfLadingInvOwner', R.prop('value', reportType) || '')),
      ]),
    }
  );

  const {
    data: projectionReportData = [],
    isError: isProjectionError,
    isFetching: isFetchingProjection = false,
    fulfilledTimeStamp: projectionTimestamp,
  } = useGetInventoryProjectionsReportQuery(
    { params },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.equals('InventoryProjections', R.prop('value', reportType))),
      ]),
    }
  );

  const {
    data: inventoryDetailsData = [],
    isFetching: isFetchingInventoryDetails = false,
    isError: isInventoryDetailsError,
    fulfilledTimeStamp: detailsTimestamp,
  } = useGetCurrentInventoryDetailsQuery(
    { params },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.equals('InventoryDetails', R.prop('value', reportType))),
      ]),
    }
  );

  const {
    data: inventorySummaryData = [],
    isError: isInventorySummaryError,
    isFetching: isFetchingInventorySummary = false,
    fulfilledTimeStamp: summaryTimestamp,
  } = useGetCurrentInventorySummaryQuery(
    { params },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.equals('InventorySummary', R.prop('value', reportType))),
        R.isNil(currentAffiliate),
      ]),
    }
  );

  const {
    data: bookingHistoryData = [],
    isError: isBookingHistoryError,
    isFetching: isFetchingBookingHistory = false,
    fulfilledTimeStamp: bookingHistoryTimestamp,
  } = useGetBookingHistoryQuery(
    { params },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.equals('BookingHistory', R.prop('value', reportType))),
        R.and(isHistorical, R.not(datesSelected)),
      ]),
    }
  );

  const {
    data: historicalInventoryData = [],
    isError: isHistoricalInventoryError,
    isFetching: isFetchingHistoricalInventory = false,
    fulfilledTimeStamp: historicalTimestamp,
  } = useGetHistoricalInventoryQuery(
    { params },
    {
      skip: R.any(R.equals(true))([
        R.isNil(R.prop('groupId', productGroup)),
        R.isNil(R.prop('groupId', allocationGroup)),
        R.not(R.equals('HistoricalInventory', R.prop('value', reportType))),
        R.and(isHistorical, R.not(datesSelected)),
      ]),
    }
  );

  const billsOfLading = isBillsOfLadingError ? [] : billsOfLadingData;
  const billsOfLadingInventoryOwner = isBillsOfLadingInventoryOwnerError
    ? []
    : billsOfLadingInventoryOwnerData;
  const projectionReport = isProjectionError ? [] : projectionReportData;
  const inventoryDetails = isInventoryDetailsError ? [] : inventoryDetailsData;
  const inventorySummary = isInventorySummaryError ? [] : inventorySummaryData;
  const bookingHistory = isBookingHistoryError ? [] : bookingHistoryData;
  const historicalInventory = isHistoricalInventoryError ? [] : historicalInventoryData;

  const exceljsPostProcess = ({ worksheet }) => {
    const { lastRow } = worksheet;
    lastRow.hidden = true;
  };

  const loadingCheck = [
    isFetchingBillsOfLading,
    isFetchingBillsOfLadingInventoryOwner,
    isFetchingProjection,
    isFetchingInventoryDetails,
    isFetchingInventorySummary,
    isFetchingBookingHistory,
    isFetchingHistoricalInventory,
  ];

  const isLoading = R.any(R.equals(true))(loadingCheck);

  const renderColumns = useMemo(
    () =>
      R.cond([
        [R.equals('BillOfLadingInvOwner'), R.always(BillsOfLadingColumns())],
        [R.equals('BillOfLadingSupplier'), R.always(BillsOfLadingColumns(true))],
        [R.equals('InventoryProjections'), R.always(InventoryProjectionsColumns)],
        [R.equals('InventoryDetails'), R.always(InventoryDetailsColumns)],
        [R.equals('InventorySummary'), R.always(InventorySummaryColumns)],
        [R.equals('BookingHistory'), R.always(BookingHistoryColumns)],
        [R.equals('HistoricalInventory'), R.always(HistoricalInventoryColumns)],
        [R.isNil, R.always([])],
      ]),
    []
  );

  const renderRows = R.cond([
    [R.equals('BillOfLadingInvOwner'), R.always(billsOfLadingInventoryOwner)],
    [R.equals('BillOfLadingSupplier'), R.always(billsOfLading)],
    [R.equals('InventoryProjections'), R.always(projectionReport)],
    [R.equals('InventoryDetails'), R.always(inventoryDetails)],
    [R.equals('InventorySummary'), R.always(inventorySummary)],
    [R.equals('BookingHistory'), R.always(bookingHistory)],
    [R.equals('HistoricalInventory'), R.always(historicalInventory)],
    [R.isNil, R.always([])],
  ]);

  const renderCheck = [allocationGroup, productGroup, reportType];
  const isReady = R.none(R.isNil, renderCheck);

  const lastUpdated =
    [
      billsOfLadingTimestamp,
      billsOfLadingInventoryOwnerTimestamp,
      projectionTimestamp,
      detailsTimestamp,
      summaryTimestamp,
      bookingHistoryTimestamp,
      historicalTimestamp,
    ]
      .filter(ts => !!ts)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] || null;

  const columns = useMemo(
    () => renderColumns(R.prop('value', reportType)).map(appendCustomOperators),
    [renderColumns, reportType]
  );
  const reportRows = isReady
    ? renderRows(R.prop('value', reportType)).map((data, idx) => ({ ...data, id: idx }))
    : [];

  const totalRowsLabel = `Total Rows: ${formatNumber(reportRows.length)}`;

  return (
    <>
      <FilterPanel
        allReports={allReports}
        filters={filters}
        reportType={reportType}
        setFilters={setFilters}
        setReportType={setReportType}
      />
      <Typography
        component="div"
        sx={{
          color: 'text.disabled',
          mb: 1.3,
          pl: 1,
          pt: { lg: 1, xs: 0 },
        }}
        variant="caption"
      >
        {lastUpdated && `Updated ${formatTz(lastUpdated, 'h:mm b zzzz', { locale: enUS })}`}&nbsp;
      </Typography>
      <Box
        sx={{
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          height: { sm: 'calc(100vh - 200px)', md: 'calc(100vh - 250px)' },
          width: '100%',
        }}
      >
        <PersistentDataGrid
          columns={columns}
          disableSelectionOnClick
          exportOptions={
            R.equals('BookingHistory', R.prop('value', reportType))
              ? { excelOptions: { exceljsPostProcess } }
              : undefined
          }
          getRowId={R.prop('id')}
          /*
              Work around for 'Filter panel breaks layout on small display #5183'
              https://github.com/mui/mui-x/issues/5183
          */
          // getRowSpacing={getRowSpacing}
          id={`inventoryReports-${reportType.value}`}
          initialState={{
            aggregation: {
              model: {
                availableReservedVol: 'sum',
                availableVol: 'sum',
                daysSupply: 'sum',
                grossVolume: 'sum',
                inTransitReservedVol: 'sum',
                inTransitVol: 'sum',
                loadableVol: 'sum',
                netVolume: 'sum',
                netVolumeInBarrels: 'sum',
                sitVol: 'sum',
                totalVol: 'sum',
                volume: 'sum',
              },
            },
          }}
          key={reportType.value}
          loading={isLoading}
          pagination={false}
          persist
          rows={reportRows}
          showFilterButton
          slotProps={{
            filterPanel: { sx: { maxWidth: '100vw' } },
          }}
          slots={{
            footer: () => (
              <Box
                sx={{
                  alignItems: 'center',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  margin: '0 auto',
                  minHeight: 52,
                  px: 3,
                  width: '100%',
                }}
              >
                <Typography variant="body2">{totalRowsLabel}</Typography>
              </Box>
            ),
          }}
        />
      </Box>
    </>
  );
};

export default Reports;
